import { UserService } from 'src/app/services/user.service';
import { Injectable } from '@angular/core';
import { Observable, of, forkJoin, BehaviorSubject, Subject } from 'rxjs';
import {
  OrganizationFeatureDtoResponse,
  OrganizationFeatureListResponseDto,
} from '../page/organization/models/organization-features';
import { DSRESTClient } from '../shared/communication-objects/restclient.service';
import { LogService } from './log.service';
import { map, take, tap } from 'rxjs/operators';
import { Cacheable, CacheBuster, globalCacheBusterNotifier } from 'ts-cacheable';


const featureCacheBuster$ = new BehaviorSubject<void>(null);
@Injectable({
  providedIn: 'root',
})
export class OrganizationFeaturesService {
  private readonly apiUrl = 'organization-features';

  constructor(
    private logger: LogService,
    private restClient: DSRESTClient,
    private userService: UserService,
  ) {}

  @Cacheable(
    {
      cacheBusterObserver: featureCacheBuster$,
    }
  )
  getPremiumServices(organizationId: number): Observable<boolean> {
    return this.restClient
      .getDirect<boolean>(
        `${this.apiUrl}/get-premium-services/${organizationId}`,
      )
      .pipe(
        tap((response) =>
          this.logger.log('getPremiumServices Response', response),
        ),
      );
  }

  setPremiumServices(
    organizationId: number,
    value: boolean,
  ): Observable<boolean> {
    return this.restClient
      .patch<boolean>(
        `${this.apiUrl}/set-premium-services/${organizationId}/${value}`,
      )
      .pipe(
        tap((response) =>
          this.logger.log('setPremiumServices Response', response),
        ),
      );
  }
  @Cacheable(
    {
      cacheBusterObserver: featureCacheBuster$,
    }
  )
  getOrganizationFeatures(
    organizationId: number,
  ): Observable<OrganizationFeatureDtoResponse> {
    return this.restClient
      .getDirect<OrganizationFeatureDtoResponse>(
        `${this.apiUrl}/features/${organizationId}`,
      )
      .pipe(
        tap((response) =>
          this.logger.log('getOrganizationFeatures Response', response),
        ),
      );
  }

  @Cacheable(
    {
      cacheBusterObserver: featureCacheBuster$,
    }
  )
  getAllFeatures(
    organizationId: number,
  ): Observable<OrganizationFeatureListResponseDto> {
    return this.restClient
      .getDirect<OrganizationFeatureListResponseDto>(
        `${this.apiUrl}/get-features/${organizationId}`,
      )
      .pipe(
        tap((response) => this.logger.log('getAllFeatures Response', response)),
      );
  }

  getPremiumUsers(organizationId: number): Observable<any> {
    // return a test list of premium users
    //return of([ { userId: 1, displayName: 'John Doe', isGroup: false }, { userId: 2, displayName: 'Jane Doe', isGroup: false } ]);
    return forkJoin({
      users: this.restClient.getDirect<any>(
        `${this.apiUrl}/get-feature-managers-users/${organizationId}`,
      ),
      groups: this.restClient.getDirect<any>(
        `${this.apiUrl}/get-feature-managers-groups/${organizationId}`,
      ),
    })
      .pipe(
        map((results) => results.users.concat(results.groups)),
        take(1),
      )
      .pipe(
        tap((response) =>
          this.logger.log('getPremiumUsers Response', response),
        ),
      );
  }

  setPremiumUsers(organizationId: number, premiumUsers: any): Observable<any> {
    globalCacheBusterNotifier.next();
    return this.restClient.post(
      `${this.apiUrl}/set-feature-managers/${organizationId}`,
      premiumUsers.map((user) => user.userId),
    );
  }

  @Cacheable(
    {
      cacheBusterObserver: featureCacheBuster$
    }
  )
  getAvailablePremiumUsers(organizationId: number) {
    //join the two lists of users and groups
    return forkJoin({
      users: this.restClient.getDirect<any>(
        `${this.apiUrl}/get-available-feature-managers-users/${organizationId}`,
      ),
      groups: this.restClient.getDirect<any>(
        `${this.apiUrl}/get-available-feature-managers-groups/${organizationId}`,
      ),
    })
      .pipe(map((results) => results.users.concat(results.groups), take(1)))
      .pipe(
        tap((response) =>
          this.logger.log('getAvailablePremiumUsers Response', response),
        ),
      );
  }

  @CacheBuster({
    cacheBusterNotifier: featureCacheBuster$,
    isInstant: true,
  })
  setFeatures(
    organizationId: number,
    features: number[],
  ): Observable<number[]> {
    globalCacheBusterNotifier.next();
    return this.restClient
      .post(`${this.apiUrl}/set-features/${organizationId}`, features)
      .pipe(
        tap((response) => this.logger.log('setFeatures Response', response)),
      );
  }

  @CacheBuster({
    cacheBusterNotifier: featureCacheBuster$,
    isInstant: true,
  })
  setOrganizationFeatures(
    organizationId: number,
    features: { [key: number]: boolean },
  ): Observable<any> {
    globalCacheBusterNotifier.next();
    return this.restClient
      .post(`${this.apiUrl}/features/${organizationId}`, features)
      .pipe(
        tap((response) =>
          this.logger.log('setOrganizationFeatures Response', response),
        ),
      );
  }

  /**
   * What turning the Affiliate Organization feature off would destroy at this billable. Reads only -
   * nothing is written until the confirmation this feeds is accepted.
   *
   * Deliberately not cached: it names other customers' organizations and is read immediately before an
   * irreversible write, so a stale answer is the one thing it must never give.
   */
  getAffiliationTeardownPreview(
    organizationId: number,
  ): Observable<IAffiliationTeardownPreview> {
    return this.restClient.getAsModel<IAffiliationTeardownPreview>(
      `${this.apiUrl}/features/${organizationId}/affiliation-teardown-preview`,
    );
  }
}

/**
 * What the teardown would clear. The named source list is a deliberate exception to the rule that the
 * receiving end of a share is never told who its sources are - it exists because this screen asks
 * someone to irreversibly delete other customers' configuration, and a bare count is not something
 * anyone can decide against.
 */
export interface IAffiliationTeardownPreview {
  billableOrganizationId: number;
  organizationsParticipating: number;
  organizationsHoldingAutoValue: number;
  affiliatedTanks: number;

  /** Tanks outside this billable affiliated into it. These belong to other customers. */
  inboundTanks: number;

  /** The organizations behind inboundTanks, grouped by the billable they belong to. */
  inboundSourceBillables: IAffiliationTeardownSourceBillable[];

  /** True when the teardown would write nothing, so the dialog can say so rather than claim work. */
  clearsNothing: boolean;
}

export interface IAffiliationTeardownSourceBillable {
  billableOrganizationId: number;
  billableOrganizationName: string;
  organizations: IAffiliationTeardownSourceOrg[];
}

export interface IAffiliationTeardownSourceOrg {
  organizationId: number;
  organizationName: string;
  autoValuePointsIntoScope: boolean;
  tanksAffiliatedIntoScope: number;
}
